import {
	createVestingTermStraightLine,
	updateVestingTermStraightLine,
	UpdateVestingTermStraightLineArgs,
} from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import {
	createOrganizationVestingTermStraightLine,
	updateOrganizationVestingTermStraightLine,
} from '@dealstack/frontend/shared/dataExchange/services/programme-defaults-vesting.service';
import {
	CreateVestingTermStraightLineDto,
	UpdateVestingTermStraightLineDto,
} from '@dealstack/shared/lib/generated';
import { useCallback, useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';

export const useStraightLineMutations = (
	refetchVestingTerms: () => Promise<unknown>,
	organizationId?: string
) => {
	const createVestingTermStraightLineRequest = useMutation({
		mutationFn: (args: CreateVestingTermStraightLineDto) =>
			organizationId
				? createOrganizationVestingTermStraightLine(organizationId, args)
				: createVestingTermStraightLine(args),
	});
	const updateVestingTermStraightLineRequest = useMutation({
		mutationFn: (args: UpdateVestingTermStraightLineArgs) =>
			organizationId
				? updateOrganizationVestingTermStraightLine(organizationId, args)
				: updateVestingTermStraightLine(args),
	});

	const onCreateVestingTermStraightLine = useCallback(
		(dto: CreateVestingTermStraightLineDto) => {
			createVestingTermStraightLineRequest.mutate(dto, {
				onSuccess: async () => {
					await refetchVestingTerms();
				},
			});
		},
		[createVestingTermStraightLineRequest, refetchVestingTerms]
	);

	const onUpdateVestingTermStraightLine = useCallback(
		(straightLineTermId: string, formValues: UpdateVestingTermStraightLineDto) => {
			updateVestingTermStraightLineRequest.mutate(
				{ straightLineTermId, ...formValues },
				{
					onSuccess: async () => {
						await refetchVestingTerms();
					},
				}
			);
		},
		[updateVestingTermStraightLineRequest, refetchVestingTerms]
	);

	return useMemo(
		() => ({
			onCreateVestingTermStraightLine,
			onUpdateVestingTermStraightLine,
		}),
		[onCreateVestingTermStraightLine, onUpdateVestingTermStraightLine]
	);
};
